/**
 * Physical Purity arithmetic: seedtrac's QC handheld Physical Purity form (deck `Physical Purity.pptx`,
 * legacy `PPReadingFormActivity`) and the QCS "QC Physical Purity Data Verification" screen.
 *
 * The analyst weighs a purity sample and splits it into fractions, each in grams:
 *
 *   Pure Seed · Inert matter · Light Seed · Discoloured seed · Cut · Small · Rough · White seed ·
 *   Germinated · Other Crop Seed ·
 *   Other Distinguishable Variety · Pin hole
 *
 * Each weight is shown as a % of the sample weight (two decimals), and each count as a number per
 * kg of seed (whole). The fractions must add back up to the sample weight.
 *
 * Pure functions shared by the API, which derives every figure on read, and both clients, which show
 * them live while the analyst types, so a percentage can never be worked out two different ways.
 */
import { z } from "zod";
import type { QualityVerdict } from "./quality";

/**
 * Where a sample's Physical Purity test has got to. Since 0114 (user, 2026-10-14) the analyst may
 * Start it and save a draft (`in_progress`) before the Submit that locks it.
 */
export const PP_STATUSES = [
  "not_applicable",
  "not_started",
  "in_progress",
  "submitted",
  "verified",
] as const;
export type PpStatus = (typeof PP_STATUSES)[number];

/** The fractions weighed, in the legacy form's order. Their grams must total the sample weight. */
export const PP_WEIGHT_FIELDS = [
  "pureG",
  "inertG",
  "lightG",
  "discolouredG",
  "cutG",
  "smallG",
  "roughG",
  "whiteG",
  "germinatedG",
  "ocsG",
  "odvG",
  "pinholeG",
] as const;
export type PpWeightField = (typeof PP_WEIGHT_FIELDS)[number];

/** Paddy Seed's Other Distinguishable Variety is counted by grain type (legacy crop branch). */
export const PADDY_ODV_FIELDS = [
  "odv1010",
  "odvFine",
  "odvBold",
  "odvLong",
  "odvOther",
] as const;
export type PaddyOdvField = (typeof PADDY_ODV_FIELDS)[number];

export const PADDY_ODV_LABELS: Record<PaddyOdvField, string> = {
  odv1010: "10-10",
  odvFine: "Fine Grain",
  odvBold: "Bold Grain",
  odvLong: "Long Grain",
  odvOther: "Other Type",
};

/** How far the fractions may miss the sample weight: the balance reads to 0.01 g. */
export const PP_WEIGHT_TOLERANCE = 0.01;

export const PP_WEIGHTS_UNBALANCED_MESSAGE =
  "Sample weight is not matched with total of all fields";

const roundTo = (value: number, dp: number): number => {
  const f = 10 ** dp;
  return Math.round((value + Number.EPSILON) * f) / f;
};

const present = (v: number | null | undefined): v is number =>
  typeof v === "number" && Number.isFinite(v);

/** A fraction's weight as a % of the sample weight, two decimals. Null until both are there. */
export function ppPct(
  grams: number | null | undefined,
  sampleWtG: number | null | undefined
): number | null {
  if (!present(grams) || !present(sampleWtG) || sampleWtG <= 0) return null;
  return roundTo((grams * 100) / sampleWtG, 2);
}

/** A count as a number per kg of seed, rounded to a whole number (legacy `Math.round`). */
export function ppPerKg(
  count: number | null | undefined,
  sampleWtG: number | null | undefined
): number | null {
  if (!present(count) || !present(sampleWtG) || sampleWtG <= 0) return null;
  return Math.round((count * 1000) / sampleWtG);
}

/** The grams of every fraction entered, two decimals (blanks count as nothing). */
export function ppWeightTotal(
  w: Partial<Record<PpWeightField, number | null | undefined>>
): number {
  return roundTo(
    PP_WEIGHT_FIELDS.reduce((sum, f) => sum + (present(w[f]) ? w[f] : 0), 0),
    2
  );
}

/** Whether the fractions add back up to the sample weight, within the balance's 0.01 g. */
export function ppWeightsBalance(
  sampleWtG: number | null | undefined,
  w: Partial<Record<PpWeightField, number | null | undefined>>
): boolean {
  if (!present(sampleWtG)) return false;
  return Math.abs(ppWeightTotal(w) - sampleWtG) <= PP_WEIGHT_TOLERANCE + 1e-9;
}

/** Paddy's ODV Total: the grain-type counts added up. Null when none was entered. */
export function paddyOdvTotal(
  c: Partial<Record<PaddyOdvField, number | null | undefined>>
): number | null {
  const got = PADDY_ODV_FIELDS.map((f) => c[f]).filter(present);
  return got.length ? got.reduce((a, b) => a + b, 0) : null;
}

/** Paddy Seed, which records ODV by grain type. Core's crop code is PD; legacy matched the name. */
export function isPaddyCrop(crop: {
  code?: string | null | undefined;
  name?: string | null | undefined;
}): boolean {
  return (
    crop.code?.trim().toUpperCase() === "PD" ||
    crop.name?.trim().toLowerCase() === "paddy seed"
  );
}

type PpNumbers = Partial<
  Record<
    | "sampleWtG"
    | PpWeightField
    | PaddyOdvField
    | "germinatedNo"
    | "ocsNo"
    | "odvNo"
    | "pinholeNo",
    number | null | undefined
  >
>;

export interface PpFigures {
  purePct: number | null;
  inertPct: number | null;
  lightPct: number | null;
  discolouredPct: number | null;
  cutPct: number | null;
  smallPct: number | null;
  roughPct: number | null;
  whitePct: number | null;
  germinatedPerKg: number | null;
  germinatedPct: number | null;
  ocsPerKg: number | null;
  ocsPct: number | null;
  /** The ODV count: the single number, or Paddy's grain-type total. */
  odvTotal: number | null;
  odvPerKg: number | null;
  odvPct: number | null;
  pinholePerKg: number | null;
  pinholePct: number | null;
  weightTotal: number;
  balanced: boolean;
}

/** Every derived figure on the form, from the typed grams and counts. */
export function ppFigures(r: PpNumbers, isPaddy: boolean): PpFigures {
  const w = r.sampleWtG;
  const odvTotal = isPaddy ? paddyOdvTotal(r) : (r.odvNo ?? null);
  return {
    purePct: ppPct(r.pureG, w),
    inertPct: ppPct(r.inertG, w),
    lightPct: ppPct(r.lightG, w),
    discolouredPct: ppPct(r.discolouredG, w),
    cutPct: ppPct(r.cutG, w),
    smallPct: ppPct(r.smallG, w),
    roughPct: ppPct(r.roughG, w),
    whitePct: ppPct(r.whiteG, w),
    germinatedPerKg: ppPerKg(r.germinatedNo, w),
    germinatedPct: ppPct(r.germinatedG, w),
    ocsPerKg: ppPerKg(r.ocsNo, w),
    ocsPct: ppPct(r.ocsG, w),
    odvTotal,
    odvPerKg: ppPerKg(odvTotal, w),
    odvPct: ppPct(r.odvG, w),
    pinholePerKg: ppPerKg(r.pinholeNo, w),
    pinholePct: ppPct(r.pinholeG, w),
    weightTotal: ppWeightTotal(r),
    balanced: ppWeightsBalance(w, r),
  };
}

/** Grams off the lab balance: two decimals at most. */
const grams = z
  .number()
  .min(0)
  .max(99_999)
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, {
    message: "Two decimals at most",
  });
const optGrams = grams.nullable();
const count = z.number().int().min(0).max(1_000_000).nullable();
const remark = z.string().trim().max(500).nullable();

/**
 * One Physical Purity reading as typed. Shape only: whether the fractions balance, and whether ODV is
 * a single number or Paddy's grain counts, depend on the sample and are checked by the server.
 */
export const ppReadingSchema = z.object({
  sampleWtG: grams.refine((v) => v > 0, { message: "Enter Sample weight" }),
  pureG: grams.refine((v) => v > 0, { message: "Enter Pure Seed" }),
  inertG: optGrams,
  lightG: optGrams,
  discolouredG: optGrams,
  // Cut / Small / Rough / White seed (user, 2026-10-30): optional so an older APK, which never
  // sends them, still submits.
  cutG: optGrams.optional(),
  smallG: optGrams.optional(),
  roughG: optGrams.optional(),
  whiteG: optGrams.optional(),
  germinatedNo: count,
  germinatedG: optGrams,
  germinatedRemarks: remark,
  ocsNo: count,
  ocsG: optGrams,
  ocsRemarks: remark,
  odvNo: count,
  odv1010: count,
  odvFine: count,
  odvBold: count,
  odvLong: count,
  odvOther: count,
  odvG: optGrams,
  odvRemarks: remark,
  pinholeNo: count,
  pinholeG: optGrams,
  pinholeRemarks: remark,
});
export type PpReading = z.infer<typeof ppReadingSchema>;

/**
 * A PP draft (Save draft on the handheld): any part of the reading, every field optional. Weights
 * that are typed must still be positive; whether they balance is checked only at Submit.
 */
export const ppDraftSchema = ppReadingSchema
  .extend({
    sampleWtG: grams
      .refine((v) => v > 0, { message: "Enter Sample weight" })
      .nullable(),
    pureG: grams
      .refine((v) => v > 0, { message: "Enter Pure Seed" })
      .nullable(),
  })
  .partial();
export type PpDraft = z.infer<typeof ppDraftSchema>;

/**
 * A crop's physical purity standards (G9), set in Masters › Crops beside Max Moisture %. Each is
 * optional; a crop with none leaves the verdict to the supervisor, as seedtrac always did.
 */
export interface PpStandards {
  minPurePct: number | null;
  maxInertPct: number | null;
  maxOcsPerKg: number | null;
  maxOdvPerKg: number | null;
}

export interface PpStandardCheck {
  key: keyof PpStandards;
  label: string;
  /** "≥ 98.00 %", "≤ 10 /kg" — the limit as the review screen prints it. */
  limitText: string;
  /** What this reading measured; null when the reading has no figure for it (counted as none). */
  value: number | null;
  ok: boolean;
}

/**
 * A reading against its crop's standards: one check per limit that is set, and the verdict they give.
 * `verdict` is null when the crop has no limit at all. A count the analyst left blank means none was
 * found, so it passes a maximum.
 */
export function ppAgainstStandards(
  f: PpFigures,
  std: PpStandards
): { checks: PpStandardCheck[]; verdict: QualityVerdict | null } {
  const checks: PpStandardCheck[] = [];
  if (std.minPurePct !== null)
    checks.push({
      key: "minPurePct",
      label: "Pure Seed %",
      limitText: `≥ ${std.minPurePct.toFixed(2)} %`,
      value: f.purePct,
      ok: f.purePct !== null && f.purePct >= std.minPurePct,
    });
  if (std.maxInertPct !== null)
    checks.push({
      key: "maxInertPct",
      label: "Inert Matter %",
      limitText: `≤ ${std.maxInertPct.toFixed(2)} %`,
      value: f.inertPct,
      ok: (f.inertPct ?? 0) <= std.maxInertPct,
    });
  if (std.maxOcsPerKg !== null)
    checks.push({
      key: "maxOcsPerKg",
      label: "Other Crop Seed /kg",
      limitText: `≤ ${std.maxOcsPerKg} /kg`,
      value: f.ocsPerKg,
      ok: (f.ocsPerKg ?? 0) <= std.maxOcsPerKg,
    });
  if (std.maxOdvPerKg !== null)
    checks.push({
      key: "maxOdvPerKg",
      label: "ODV /kg",
      limitText: `≤ ${std.maxOdvPerKg} /kg`,
      value: f.odvPerKg,
      ok: (f.odvPerKg ?? 0) <= std.maxOdvPerKg,
    });
  return {
    checks,
    verdict: checks.length
      ? checks.every((c) => c.ok)
        ? "acceptable"
        : "not_acceptable"
      : null,
  };
}
